"use client"

import { Suspense, useMemo, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useQuery, useQueries } from "@tanstack/react-query"
import { TrendingUp, TrendingDown, Minus } from "lucide-react"
import { cn } from "@/lib/utils"
import { getStocks } from "@/lib/api/stocks"
import { getFinancialYears, getFinancials } from "@/lib/api/financials"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"
import type { FinancialStatement } from "@/types/financial"

// ── 상수 ───────────────────────────────────────────────────────────────────────

const QUARTER_REPRT_CODES = ["11013", "11012", "11014"] as const

const REPRT_LABEL: Record<string, string> = {
  "11013": "1Q",
  "11012": "2Q",
  "11014": "3Q",
}

// 1Q→2Q→3Q 명시적 정렬 순서 (localeCompare 시 11012<11013<11014 → 2Q→1Q→3Q 오류 방지)
const REPRT_ORDER: Record<string, number> = { "11013": 1, "11012": 2, "11014": 3 }

const SJ_DIVS = [
  { key: "",   label: "전체"       },
  { key: "IS", label: "손익계산서" },
  { key: "BS", label: "재무상태표" },
  { key: "CF", label: "현금흐름표" },
] as const

const MAIN_TABS = [
  { key: "period", label: "기간별 조회" },
  { key: "trend",  label: "추세 조회"  },
] as const

type MainTab = (typeof MAIN_TABS)[number]["key"]

// ── 헬퍼 ───────────────────────────────────────────────────────────────────────

function fmtAmount(n: number | null | undefined): string {
  if (n == null) return "-"
  return n.toLocaleString("ko-KR")
}

function amountColor(n: number | null | undefined): string {
  if (n == null) return "text-zinc-400"
  return n < 0 ? "text-blue-400" : n > 0 ? "text-red-400" : "text-zinc-400"
}

function findMetricAmount(rows: FinancialStatement[], match: string): number | null {
  return rows.find((r) => r.accountNm.includes(match))?.amount ?? null
}

function calcDebtRatio(rows: FinancialStatement[]): number | null {
  const liabilities = rows.find((r) => r.accountNm === "부채총계")?.amount
  const equity      = rows.find((r) => r.accountNm === "자본총계")?.amount
  if (liabilities == null || equity == null || equity === 0) return null
  return Math.round((liabilities / equity) * 100)
}

function DeltaIcon({ curr, prev }: { curr: number | null; prev: number | null }) {
  if (curr == null || prev == null) return <Minus className="size-3 text-zinc-600" />
  if (curr > prev) return <TrendingUp   className="size-3 text-red-400"  />
  if (curr < prev) return <TrendingDown className="size-3 text-blue-400" />
  return <Minus className="size-3 text-zinc-600" />
}

// 억원 단위 포맷 (원 단위 입력)
function fmtEok(v: number | null | undefined): string {
  if (v == null) return "-"
  const eok = v / 100_000_000
  const abs  = Math.abs(eok)
  if (abs >= 10_000) return `${(eok / 10_000).toFixed(1)}조`
  if (abs >= 1)      return `${Math.round(eok).toLocaleString("ko-KR")}억`
  return `${Math.round(v / 1_000_000).toLocaleString("ko-KR")}백만`
}

function eokColor(n: number | null | undefined): string {
  if (n == null || n === 0) return "text-zinc-400"
  return n < 0 ? "text-red-400" : "text-zinc-300"
}

function reprtLabel(reprt: string, year: string): string {
  return `${year.slice(2)}/${REPRT_LABEL[reprt] ?? reprt}`
}

// ── 주식 검색 컴포넌트 ──────────────────────────────────────────────────────────

function StockSearchBar({
  value,
  onChange,
}: {
  value: { code: string; name: string } | null
  onChange: (v: { code: string; name: string } | null) => void
}) {
  const [query, setQuery] = useState("")
  const [open,  setOpen]  = useState(false)

  const { data: stocks = [] } = useQuery({
    queryKey: ["stocks"],
    queryFn:  getStocks,
    staleTime: 300_000,
  })

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return stocks.slice(0, 30)
    return stocks
      .filter((s) => s.stockCode.includes(q) || s.stockName.toLowerCase().includes(q))
      .slice(0, 30)
  }, [stocks, query])

  return (
    <div className="relative w-72">
      <input
        className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-emerald-500"
        placeholder="종목명 또는 코드 검색…"
        value={value ? `${value.name} (${value.code})` : query}
        onFocus={() => { onChange(null); setQuery(""); setOpen(true) }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-lg">
          {filtered.map((s) => (
            <li
              key={s.stockCode}
              className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-zinc-800"
              onMouseDown={() => {
                onChange({ code: s.stockCode, name: s.stockName })
                setQuery("")
                setOpen(false)
              }}
            >
              <span className="text-zinc-100">{s.stockName}</span>
              <span className="font-mono text-xs text-zinc-500">{s.stockCode}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── 기간별 조회 탭 ──────────────────────────────────────────────────────────────

function PeriodTab({ code }: { code: string }) {
  const [sjDiv, setSjDiv] = useState("IS")

  const cy = String(new Date().getFullYear())
  const py = String(new Date().getFullYear() - 1)

  const { data: years = [], isLoading: yearsLoading } = useQuery<string[]>({
    queryKey: ["financials", code, "years"],
    queryFn:  () => getFinancialYears(code),
    enabled:  !!code,
  })

  // 연간: 최신 5년
  const annualYears = years.slice(0, 5)

  // 분기: 전년 + 당해 × 3개 코드
  const quarterDefs = [py, cy].flatMap(y =>
    QUARTER_REPRT_CODES.map(r => ({ year: y, reprt: r }))
  )

  const annualResults = useQueries({
    queries: annualYears.map(y => ({
      queryKey: ["financials", code, y, "11011", sjDiv],
      queryFn:  () => getFinancials(code, y, "11011", sjDiv || undefined),
      enabled:  !!code && annualYears.length > 0,
    })),
  })

  const quarterResults = useQueries({
    queries: quarterDefs.map(({ year, reprt }) => ({
      queryKey: ["financials", code, year, reprt, sjDiv],
      queryFn:  () => getFinancials(code, year, reprt, sjDiv || undefined),
      enabled:  !!code,
    })),
  })

  const allLoading = yearsLoading ||
    annualResults.some(r => r.isLoading) ||
    quarterResults.some(r => r.isLoading)

  // 데이터가 있는 분기 컬럼만, 연도 오름차순 → 분기 1Q→2Q→3Q 명시 정렬
  const visibleQuarters = quarterDefs
    .map((def, i) => ({ def, data: quarterResults[i]?.data ?? [] }))
    .filter(({ data }) => data.length > 0)
    .sort((a, b) =>
      a.def.year !== b.def.year
        ? Number(a.def.year) - Number(b.def.year)
        : (REPRT_ORDER[a.def.reprt] ?? 9) - (REPRT_ORDER[b.def.reprt] ?? 9)
    )

  // 계정과목 순서: 전체 결과에서 첫 등장 순
  const seen = new Set<string>()
  const accountOrder: { id: string; nm: string }[] = []
  for (const rows of [
    ...annualResults.map(r => r.data ?? []),
    ...visibleQuarters.map(q => q.data),
  ]) {
    for (const row of rows) {
      if (!seen.has(row.accountId)) {
        seen.add(row.accountId)
        accountOrder.push({ id: row.accountId, nm: row.accountNm })
      }
    }
  }

  const annualMaps  = annualResults.map(r =>
    new Map((r.data ?? []).map(row => [row.accountId, row.amount]))
  )
  const quarterMaps = visibleQuarters.map(({ data }) =>
    new Map(data.map(row => [row.accountId, row.amount]))
  )

  const totalCols = annualYears.length + visibleQuarters.length

  return (
    <div className="flex flex-col gap-3">
      {/* 재무제표 종류 필터 */}
      <div className="flex gap-1">
        {SJ_DIVS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSjDiv(key)}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors",
              sjDiv === key
                ? "bg-emerald-500/20 text-emerald-400"
                : "text-zinc-400 hover:text-zinc-100"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 테이블 */}
      <div className="rounded-lg border border-zinc-800 overflow-x-auto">
        <Table>
          <TableHeader>
            {/* 1행: 연간 / 분기 그룹 */}
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead rowSpan={2} className="align-middle text-zinc-400">
                계정과목
              </TableHead>
              {annualYears.length > 0 && (
                <TableHead
                  colSpan={annualYears.length}
                  className="text-center text-xs font-medium text-zinc-500"
                >
                  연간
                </TableHead>
              )}
              {visibleQuarters.length > 0 && (
                <TableHead
                  colSpan={visibleQuarters.length}
                  className="border-l-2 border-primary text-center text-xs font-medium text-zinc-500"
                >
                  분기
                </TableHead>
              )}
            </TableRow>
            {/* 2행: 개별 연도 / 분기 레이블 */}
            <TableRow className="border-b border-zinc-800 hover:bg-transparent">
              {annualYears.map(y => (
                <TableHead key={y} className="w-24 text-right text-xs text-zinc-400">
                  {y}
                </TableHead>
              ))}
              {visibleQuarters.map(({ def }, i) => (
                <TableHead
                  key={`${def.year}-${def.reprt}`}
                  className={cn(
                    "w-20 text-right text-xs text-zinc-400",
                    i === 0 && "border-l-2 border-primary"
                  )}
                >
                  {reprtLabel(def.reprt, def.year)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {allLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i} className="border-zinc-800">
                  {Array.from({ length: Math.max(totalCols, 6) + 1 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full bg-zinc-800" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : accountOrder.length === 0 ? (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={totalCols + 1} className="py-10 text-center text-zinc-500">
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              accountOrder.map(({ id, nm }) => (
                <TableRow key={id} className="border-zinc-800 hover:bg-zinc-800/40">
                  <TableCell className="text-sm text-zinc-300">{nm}</TableCell>
                  {annualMaps.map((map, i) => {
                    const v = map.get(id) ?? null
                    return (
                      <TableCell key={annualYears[i]} className={cn("text-right font-mono text-xs", eokColor(v))}>
                        {fmtEok(v)}
                      </TableCell>
                    )
                  })}
                  {quarterMaps.map((map, i) => {
                    const { def } = visibleQuarters[i]
                    const v = map.get(id) ?? null
                    return (
                      <TableCell
                        key={`${def.year}-${def.reprt}`}
                        className={cn(
                          "text-right font-mono text-xs",
                          eokColor(v),
                          i === 0 && "border-l-2 border-primary"
                        )}
                      >
                        {fmtEok(v)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// ── 추세 조회 탭 ────────────────────────────────────────────────────────────────

type YearMetrics = {
  year:      string
  revenue:   number | null
  opIncome:  number | null
  netIncome: number | null
  cfOper:    number | null
  debtRatio: number | null
}

function TrendTab({ code }: { code: string }) {
  const { data: years = [], isLoading: yearsLoading } = useQuery<string[]>({
    queryKey: ["financials", code, "years"],
    queryFn:  () => getFinancialYears(code),
    enabled:  !!code,
  })

  const displayYears = years.slice(0, 6)

  // 연간 전체 데이터 (sj_div 없이 조회 — fallback)
  const allResults = useQueries({
    queries: displayYears.map((y) => ({
      queryKey: ["financials", code, y, "11011", "all"],
      queryFn:  () => getFinancials(code, y, "11011"),
      enabled:  !!code && displayYears.length > 0,
    })),
  })

  const isLoading = yearsLoading || allResults.some((r) => r.isLoading)

  const yearData: YearMetrics[] = displayYears.map((y, i) => {
    const rows: FinancialStatement[] = allResults[i]?.data ?? []
    return {
      year:      y,
      revenue:   findMetricAmount(rows, "매출액"),
      opIncome:  findMetricAmount(rows, "영업이익"),
      netIncome: findMetricAmount(rows, "당기순이익"),
      cfOper:    findMetricAmount(rows, "영업활동"),
      debtRatio: calcDebtRatio(rows),
    }
  })

  const metrics: { label: string; getValue: (d: YearMetrics) => number | null; isRatio?: boolean }[] = [
    { label: "매출액",           getValue: (d) => d.revenue   },
    { label: "영업이익",         getValue: (d) => d.opIncome  },
    { label: "당기순이익",       getValue: (d) => d.netIncome },
    { label: "영업활동현금흐름",  getValue: (d) => d.cfOper   },
    { label: "부채비율(%)",       getValue: (d) => d.debtRatio, isRatio: true },
  ]

  return (
    <div className="rounded-lg border border-zinc-800 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800 hover:bg-transparent">
            <TableHead className="w-36 text-zinc-400">지표</TableHead>
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <TableHead key={i} className="text-right">
                    <Skeleton className="h-4 w-12 bg-zinc-800 ml-auto" />
                  </TableHead>
                ))
              : displayYears.map((y) => (
                  <TableHead key={y} className="w-28 text-right text-zinc-400">{y}</TableHead>
                ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i} className="border-zinc-800">
                {Array.from({ length: (displayYears.length || 4) + 1 }).map((_, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full bg-zinc-800" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            metrics.map(({ label, getValue, isRatio }) => (
              <TableRow key={label} className="border-zinc-800 hover:bg-zinc-800/40">
                <TableCell className="text-sm font-medium text-zinc-300">{label}</TableCell>
                {yearData.map((d, i) => {
                  const curr = getValue(d)
                  const prev = i < yearData.length - 1 ? getValue(yearData[i + 1]) : null
                  return (
                    <TableCell key={d.year} className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <DeltaIcon curr={curr} prev={prev} />
                        <span className={cn("font-mono text-sm", isRatio ? "text-zinc-300" : amountColor(curr))}>
                          {isRatio
                            ? curr != null ? `${curr}%` : "-"
                            : fmtAmount(curr)}
                        </span>
                      </div>
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

// ── 메인 페이지 ─────────────────────────────────────────────────────────────────

function FinancialsContent() {
  const router = useRouter()
  const params = useSearchParams()
  const codeParam = params.get("code")

  const { data: stocks = [] } = useQuery({
    queryKey: ["stocks"],
    queryFn:  getStocks,
    staleTime: 300_000,
  })

  const initStock = useMemo(() => {
    if (!codeParam) return null
    const s = stocks.find((x) => x.stockCode === codeParam)
    return s ? { code: s.stockCode, name: s.stockName } : { code: codeParam, name: codeParam }
  }, [codeParam, stocks])

  const [selected, setSelected] = useState<{ code: string; name: string } | null>(null)
  const [mainTab,  setMainTab]  = useState<MainTab>("period")

  const effectiveStock = selected ?? initStock

  const handleSelect = (v: { code: string; name: string } | null) => {
    setSelected(v)
    router.replace(v ? `/financials?code=${v.code}` : "/financials")
  }

  return (
    <div className="flex flex-col gap-4">

      {/* 상단 종목 검색 */}
      <div className="flex items-center gap-3">
        <StockSearchBar value={effectiveStock} onChange={handleSelect} />
        {effectiveStock && (
          <span className="text-sm text-zinc-400">
            {effectiveStock.name}
            <span className="ml-2 font-mono text-xs text-zinc-600">{effectiveStock.code}</span>
          </span>
        )}
      </div>

      {/* 메인 탭 */}
      <div className="flex border-b border-zinc-800">
        {MAIN_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setMainTab(key)}
            className={cn(
              "min-h-[40px] px-6 text-sm font-medium transition-colors border-b-2 -mb-px",
              mainTab === key
                ? "border-emerald-400 text-emerald-400"
                : "border-transparent text-zinc-400 hover:text-zinc-100"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      {!effectiveStock ? (
        <div className="flex h-48 items-center justify-center text-zinc-500">
          종목을 선택하세요.
        </div>
      ) : mainTab === "period" ? (
        <PeriodTab code={effectiveStock.code} />
      ) : (
        <TrendTab code={effectiveStock.code} />
      )}
    </div>
  )
}

export default function FinancialsPage() {
  return (
    <Suspense>
      <FinancialsContent />
    </Suspense>
  )
}
